import React from 'react';
import { Button } from 'react-bootstrap';
import ReceptionModalShell, { ReceptionModalSection } from './ui/ReceptionModalShell';

export default function ReceptionGuideModal({ show, onHide }) {
  return (
    <ReceptionModalShell
      show={show}
      onHide={onHide}
      size="lg"
      title="Guide d'accueil"
      subtitle="Réception SMIE — repères pour le flux quotidien"
      footer={
        <Button variant="secondary" onClick={onHide}>
          Fermer
        </Button>
      }
    >
      <ReceptionModalSection title="1. Ouverture de session">
        <ul className="recv-guide-list mb-0">
          <li>
            À la connexion, <strong>choisissez votre site de travail</strong> parmi ceux affectés à votre compte.
          </li>
          <li>
            Le site actif apparaît en badge dans le header (cliquez dessus pour le changer). Les passages et
            orientations utilisent ce site par défaut.
          </li>
        </ul>
      </ReceptionModalSection>

      <ReceptionModalSection title="2. Navigation">
        <ul className="recv-guide-list mb-0">
          <li>
            <strong>Ordinateur</strong> : menu latéral (repliable via « Replier » en bas). Survol d&apos;une icône =
            infobulle avec le libellé.
          </li>
          <li>
            <strong>Tablette / mobile</strong> : barre horizontale sous le header (Accueil, RDV, Reporting…) + menu ☰
            pour le détail complet et le guide.
          </li>
          <li>
            Pages disponibles : <strong>Tableau de bord</strong>, <strong>Rendez-vous</strong>,{' '}
            <strong>Reporting & historique</strong>, <strong>Sociétés</strong>, <strong>Adhérents</strong>,{' '}
            <strong>Membres externes</strong>.
          </li>
        </ul>
      </ReceptionModalSection>

      <ReceptionModalSection title="3. Tableau de bord — identifier un patient">
        <ul className="recv-guide-list mb-0">
          <li>
            Recherchez par <strong>matricule, nom, prénom ou téléphone</strong> (minimum 2 caractères). Les résultats
            affichent le <strong>sexe</strong> et l&apos;<strong>âge</strong> (ex. 42 ans, 36M, 36M et 7j pour les
            moins de 3 ans).
          </li>
          <li>
            Cliquez <strong>Sélectionner</strong> sur la bonne ligne (adhérent SMIE ou membre externe). La fiche patient
            s&apos;affiche avec les <strong>5 derniers passages</strong> lorsque le matricule est connu.
          </li>
          <li>
            Actions possibles : <strong>Enregistrer et orienter</strong> (passage + orientation en une étape) ou{' '}
            <strong>Nouveau RDV</strong> pour planifier un créneau.
          </li>
          <li>
            <strong>Nouveau foyer</strong> ou <strong>membre externe (rapide)</strong> : boutons sous la recherche pour
            créer un patient sans quitter le tableau de bord.
          </li>
        </ul>
      </ReceptionModalSection>

      <ReceptionModalSection title="4. Enregistrer et orienter">
        <ul className="recv-guide-list mb-0">
          <li>
            Choisissez la <strong>filière</strong> : médecine, dentiste ou infirmerie. Les libellés et la liste des
            professionnels s&apos;adaptent.
          </li>
          <li>
            Le <strong>site de passage est obligatoire</strong> (site de session pré-rempli si possible). Le
            professionnel reste <strong>facultatif</strong> : sans choix, le patient entre en file d&apos;attente au site
            pour l&apos;équipe soignante.
          </li>
          <li>
            Vous pouvez saisir des <strong>motifs / notes</strong> avant validation. Le passage apparaît dans la{' '}
            <strong>file du jour</strong> et la consultation est créée côté soins.
          </li>
        </ul>
      </ReceptionModalSection>

      <ReceptionModalSection title="5. File du jour & consultations orientées">
        <ul className="recv-guide-list mb-0">
          <li>
            <strong>Passages orientés</strong> (colonne droite du tableau de bord) : liste des passages enregistrés et
            orientés <strong>aujourd&apos;hui</strong> pour le site actif (heure, filière, statut consultation).
          </li>
          <li>
            <strong>Consultations orientées</strong> (bas de page) : suivi des consultations créées après orientation,
            filtrées par site, avec pagination.
          </li>
          <li>
            Utilisez <strong>Actualiser</strong> si la liste ne se met pas à jour après une action.
          </li>
        </ul>
      </ReceptionModalSection>

      <ReceptionModalSection title="6. Rendez-vous">
        <ul className="recv-guide-list mb-0">
          <li>
            Filtrez par période (aujourd&apos;hui, 7 jours, à venir…), statut, type de RDV ou recherche texte.
          </li>
          <li>
            Créez ou modifiez un RDV : patient SMIE ou externe, type (médecin, dentiste, infirmerie, autre service),
            praticien optionnel selon le mode choisi.
          </li>
          <li>
            Ouvrez le <strong>détail</strong> d&apos;un RDV pour voir la fiche, <strong>changer le statut</strong>{' '}
            (confirmé, honoré, annulé…) ou lancer <strong>Enregistrer visite</strong> (passage + orientation automatique
            — le site de passage est alors obligatoire).
          </li>
          <li>
            <strong>Export CSV</strong> : exporte la liste filtrée courante pour archivage ou suivi administratif.
          </li>
        </ul>
      </ReceptionModalSection>

      <ReceptionModalSection title="7. Annuaires">
        <ul className="recv-guide-list mb-0">
          <li>
            <strong>Sociétés</strong> : annuaire des partenaires actifs (recherche, pagination).
          </li>
          <li>
            <strong>Adhérents</strong> : liste des adhérents actifs avec sexe et âge. Bouton <strong>Détail</strong>{' '}
            pour la fiche (identité, derniers passages). Depuis la fiche, <strong>Modifier</strong> permet de corriger
            nom, prénom, sexe, fonction et date de naissance (matricule, société et type restent réservés à
            l&apos;administration).
          </li>
          <li>
            <strong>Membres externes</strong> : création, recherche, modification et suppression des patients hors SMIE.
          </li>
          <li>
            <strong>Nouveau foyer</strong> (adhérents ou tableau de bord) : rattacher des ayants droit à un agent
            existant (matricules et règles métier automatiques).
          </li>
        </ul>
      </ReceptionModalSection>

      <ReceptionModalSection title="8. Reporting & historique">
        <ul className="recv-guide-list mb-0">
          <li>
            Consultez le journal des passages sur une <strong>plage de dates</strong>, avec filtres société, filière,
            statut et recherche texte.
          </li>
          <li>
            Pagination jusqu&apos;à 50 lignes par page. L&apos;<strong>export CSV</strong> inclut{' '}
            <strong>tous</strong> les résultats correspondant aux filtres actifs, pas seulement la page affichée.
          </li>
        </ul>
      </ReceptionModalSection>

      <ReceptionModalSection title="9. RDV → visite (rappel mapping)">
        <ul className="recv-guide-list mb-0">
          <li>
            Lors de l&apos;enregistrement depuis un RDV, le type est converti automatiquement :{' '}
            <strong>Médecin → MEDICAL</strong>, <strong>Dentiste → DENTAL</strong>, <strong>Infirmerie → NURSING</strong>
            , <strong>Autre service → MEDICAL</strong>.
          </li>
        </ul>
      </ReceptionModalSection>

      <ReceptionModalSection title="10. Paramètres (administration)">
        <ul className="recv-guide-list mb-0">
          <li>
            Les comptes administration peuvent définir l&apos;<strong>orientation par défaut</strong> (dentiste,
            médecine ou infirmerie) en bas du menu latéral — elle pré-remplit la fenêtre « Enregistrer et orienter ».
          </li>
        </ul>
      </ReceptionModalSection>
    </ReceptionModalShell>
  );
}
